import {useEffect, useMemo} from 'react'
import _ from 'lodash'
import {useConvertAPI} from '../index'

/**
 * Hook for Convert.com advanced page tagging.
 *
 * Sets global _conv_ variables that Convert.com uses for experiment targeting.
 * Automatically manages the lifecycle of these variables - setting them when
 * the component mounts and cleaning them up when it unmounts.
 *
 * Key behaviors:
 * - Maps tag properties to snake_case _conv_ global variables
 * - Special mapping: productId → _conv_product_sku
 * - Only sets variables for non-null, non-empty values (including 0)
 * - Deletes variables when values become null/undefined/empty
 * - Preserves other _conv_ variables not managed by this hook
 * - Automatically cleans up managed variables on component unmount
 *
 * @see https://support.convert.com/hc/en-us/articles/205152665-targeting-with-advanced-page-tagging
 * @param {
 *   | {pageType: 'homepage'|'search result'|'cart'|'checkout'}
 *   | {pageType: 'product list', categoryId?: string, categoryName?: string}
 *   | {pageType: 'product detail', productId?: string, productName?: string}
 * } tags - Page tagging configuration object
 * @throws {Error} When tags is null/undefined or missing pageType
 */
const useConvertPageTags = (tags) => {
    if (!tags?.pageType) {
        throw new Error('useConvertPageTags must be used with a pageType')
    }

    const {isDisabled: isConvertApiDisabled} = useConvertAPI()

    // Disable the hook if Convert API is disabled or if no page type tag is provided
    const isHookDisabled = isConvertApiDisabled || !tags?.pageType

    // Stringify tags to prevent unnecessary effect re-runs when object reference changes
    // but content is the same
    const memoizedTagsJson = useMemo(() => JSON.stringify(tags), [tags])

    useEffect(() => {
        if (isHookDisabled) {
            return
        }

        const parsedTags = JSON.parse(memoizedTagsJson)

        // Track which global variables this hook instance is responsible for
        const managedGlobalKeys = new Set()

        // Process each tag and set/delete corresponding global variables
        Object.entries(parsedTags).forEach(([tagKey, tagValue]) => {
            // Convert tag key to Convert.com global variable name
            // Examples: pageType → _conv_page_type, productId → _conv_product_sku
            const globalVariableName =
                tagKey === 'productId' ? '_conv_product_sku' : `_conv_${_.snakeCase(tagKey)}`

            if (tagValue != null && tagValue !== '') {
                // Set global variable for valid values (including number 0)
                global[globalVariableName] = tagValue
                managedGlobalKeys.add(globalVariableName)
            } else {
                // Delete global variable for null/undefined/empty values
                // This handles cases where a tag value changes from valid to invalid
                delete global[globalVariableName]
            }
        })

        // Cleanup function: remove only the variables this hook instance manages
        return () => {
            managedGlobalKeys.forEach((globalKey) => {
                delete global[globalKey]
            })
        }
    }, [isHookDisabled, memoizedTagsJson])
}

export default useConvertPageTags
